import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Button } from 'reactstrap';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import Sidebar from '../../Sidebar';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import FloatingControls from '../../components/FloatingControls';
import { displayError } from '../../helpers';
import {
    cancelPaymentVoucher,
    deletePaymentVoucher,
    getPaymentVoucherDetails,
} from '../../services/PaymentVoucherServices';
import { AccountingHintPanel } from './VoucherUtils';

function PaymentVoucherEdit() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [record, setRecord] = useState(null);
    const [inProgress, setInProgress] = useState(true);

    useEffect(() => {
        if (!id) {
            return;
        }
        setInProgress(true);
        getPaymentVoucherDetails(cookies, id)
            .then((response) => setRecord(response))
            .catch((error) => displayError(error?.response?.data || error, 'Load Failed'))
            .finally(() => setInProgress(false));
    }, [cookies, id]);

    const handleCancel = () => {
        Swal.fire({
            title: 'Cancel payment voucher?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            cancelPaymentVoucher(cookies, id)
                .then((response) => {
                    Swal.fire('Cancelled', response.message || 'Payment voucher cancelled.', 'success');
                    return getPaymentVoucherDetails(cookies, id);
                })
                .then((response) => setRecord(response))
                .catch((error) => displayError(error?.response?.data || error, 'Cancel Failed'))
                .finally(() => setInProgress(false));
        });
    };

    const handleDelete = () => {
        Swal.fire({
            title: 'Delete cancelled payment voucher?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            deletePaymentVoucher(cookies, id)
                .then((response) => {
                    Swal.fire('Deleted', response.message || 'Payment voucher deleted.', 'success');
                    navigate('/PaymentVoucherTable');
                })
                .catch((error) => displayError(error?.response?.data || error, 'Delete Failed'))
                .finally(() => setInProgress(false));
        });
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <FormPageLayout
                    moduleLabel="ACCOUNTS"
                    title="Payment Voucher"
                    subtitle={record?.payment_no || ''}
                    modeLabel="VIEW MODE"
                >
                    {record && (
                        <>
                            <AccountingHintPanel hint={record.accounting_hint} />
                            <div className="form-row table-bordered shadow p-3 my-2 border-secondary form-control-panel">
                                <div className="row mb-2">
                                    <div className="col-sm-3"><strong>Type</strong><div>{record.payment_type_label || record.payment_type}</div></div>
                                    <div className="col-sm-3"><strong>Plant</strong><div>{record.plant_alias}</div></div>
                                    <div className="col-sm-3"><strong>Payment No</strong><div>{record.payment_no}</div></div>
                                    <div className="col-sm-3"><strong>Status</strong><div>{record.status ? 'Active' : 'Cancelled'}</div></div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-sm-4"><strong>Party</strong><div>{record.party_name}</div></div>
                                    <div className="col-sm-4"><strong>Bank</strong><div>{record.bank_name}</div></div>
                                    <div className="col-sm-4"><strong>Amount</strong><div>{record.payment_amount}</div></div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-sm-3"><strong>Payment Date</strong><div>{record.payment_date}</div></div>
                                    <div className="col-sm-3"><strong>Mode</strong><div>{record.payment_mode}</div></div>
                                    <div className="col-sm-3"><strong>Against</strong><div>{record.payment_against}</div></div>
                                    <div className="col-sm-3"><strong>Bank Date</strong><div>{record.bank_date}</div></div>
                                    <div className="col-sm-3"><strong>Instrument</strong><div>{record.instrument_no || '—'}</div></div>
                                </div>
                                {record.remarks && (
                                    <div className="row"><div className="col-sm-12"><strong>Remarks</strong><div>{record.remarks}</div></div></div>
                                )}
                            </div>
                            {(record.document_lines || []).length > 0 && (
                                <div className="table-responsive form-control-panel shadow p-2 my-2">
                                    <table className="table table-sm table-bordered mb-0">
                                        <thead>
                                            <tr>
                                                <th>Document</th>
                                                <th>Payment Amt</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {record.document_lines.map((line) => (
                                                <tr key={line.gmr_id || line.journal_id}>
                                                    <td>{line.reference_no}</td>
                                                    <td>{line.payment_amt}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                    <FloatingControls>
                        {record?.status ? (
                            <Button color="warning" type="button" onClick={handleCancel}>Cancel Voucher</Button>
                        ) : (
                            <Button color="danger" type="button" onClick={handleDelete}>Delete</Button>
                        )}
                        <Button color="secondary" type="button" onClick={() => navigate('/PaymentVoucherTable')}>Back to List</Button>
                    </FloatingControls>
                </FormPageLayout>
                <LoadingOverlay active={inProgress} />
            </div>
        </div>
    );
}

export default PaymentVoucherEdit;
